import React, { useLayoutEffect, type ReactNode } from 'react';
import { useQueryLoader, type PreloadedQuery } from 'react-relay';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { SPA_CONTENT } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { jiraSpaPageContainerResource } from '@atlassian/jira-router-resources-spa-page-container/src/jiraSpaPageContainerResource.tsx';
import type { spaEntryPoint } from '../../../../entrypoint.tsx';
import type { PageContainerProps } from './types.tsx';
import { GlobalContextProvider } from './GlobalContextProvider.tsx';
import { JiraSpaPageContainer as JiraSpaPageContainerInner } from './JiraSpaPageContainer.tsx';
import QUERY, {
	type JiraSpaPageContainerQuery,
} from './__generated__/JiraSpaPageContainerQuery.graphql';

/**
 * Glues whatever the SSR service did with this route's configuration in to the CSR render process.
 *
 * @note
 * The page layout has its own data query, which is preloaded during SSR.
 * This component attempts to load the query via entrypoint definition first.
 * If that does not exist, it falls back to a route-resource method.
 * If that does not work, it falls back to loading the query lazily.
 */
const LoadViaSideEffect = (props: { children: ReactNode }) => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// Check both of our mechanisms for initiating queries ahead of time
	const spaPageEntryPointReference = usePageLayoutEntryPoint<typeof spaEntryPoint>(SPA_CONTENT);
	const { queryReference: queryReferenceViaRouteResource } =
		useRelayResource<JiraSpaPageContainerQuery>(jiraSpaPageContainerResource);
	// Condense the two mechanisms in to a single preloaded query reference (or null)
	const initialQueryReference: PreloadedQuery<JiraSpaPageContainerQuery> | null =
		spaPageEntryPointReference?.queries?.queryReference || queryReferenceViaRouteResource || null;
	// Invoke query loader, which will either use the (now-found) preloaded query, or a null query reference
	const [queryReference, loadQuery] = useQueryLoader<JiraSpaPageContainerQuery>(
		QUERY,
		initialQueryReference,
	);
	// We lean on this query having been kicked off via a route resource or a nested page entrypoint.
	// If we're in the client and it hasn't been started, we need to force the query to load lazily.
	// This would typically happen on a static fallback page or a misconfigured route.
	// This effect wouldn't ever run during SSR, so don't worry about that.
	useLayoutEffect(() => {
		if (!queryReference) {
			const event = createAnalyticsEvent({});
			fireOperationalAnalytics(event, 'jiraSpaPageContainerBootstrapper fallback', {
				isSpa: !!globalThis.__SPA__,
				// @ts-expect-error it exists most of the time
				hasSpaState: !!globalThis.SPA_STATE,
				// @ts-expect-error it exists some of the time
				isSsrFallback: !!globalThis.__SSR_FALLBACK__,
				ssrDetails: globalThis.SSR_DETAILS,
			});
			loadQuery({ cloudId });
		}
	});
	return (
		<JiraSpaPageContainerInner queryReference={queryReference}>
			{props.children}
		</JiraSpaPageContainerInner>
	);
};

/**
 * Kicks off the render for the Jira SPA page layout.
 * The Jira app is a sort-of islands architecture, where each route is composed from multiple "page" entrypoints.
 * Each of these is expected to render its own suspense boundary and loading skeleton.
 *
 * @note
 * When altering how this component or any of its children render, be sure you test the following scenarios:
 *
 * 1. A page created using the `createPageEntryPoint` utility (e.g., issueview)
 * 2. A page created via the `createChromelessEntryPoint` utility (e.g., most embed routes)
 * 3. A "legacy" page rendering via monolith (e.g., the /plugins/servlet/upm route)
 *
 * Also test that the page renders correctly when various SSR scenarios have occurred:
 *
 * 1. Full SSR success
 * 2. Static fallback
 * 3. SSR shell renders, route fails
 * 4. SSR shell renders, route partially renders
 */
export const JiraSpaPageContainerBootstrapper = ({ children, ...rest }: PageContainerProps) => {
	return (
		<GlobalContextProvider {...rest}>
			<LoadViaSideEffect>{children}</LoadViaSideEffect>
		</GlobalContextProvider>
	);
};
